import React, { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParamsContext } from '../../contexts/ParamsContext';
import { CalendarDays, CircleDot, EllipsisVertical, History, MessageCircle, MessageCircleMore, SquarePen, Trash2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface Option {
    type: string;
    label?: string;
    icon?: LucideIcon;
    onClick?: (row: any) => void;
    disabled?: boolean;
}

interface TableActionsProps {
    row?: any;
    dropdown?: boolean;
    options?: Option[];
    children?: ReactNode;
    disabled?: boolean;
}

const MENU_WIDTH = 224;
const MENU_GUTTER = 8;
const MENU_OFFSET = 8;

const getOptionLabel = (type: string, label?: string) => {
    if (label) return label;

    switch (type.toLowerCase()) {
        case 'edit':
        case 'editar':
            return 'Editar';
        case 'delete':
        case 'remover':
            return 'Remover';
        case 'history':
        case 'histórico':
        case 'historico':
            return 'Histórico';
        case 'mensagem':
            return 'Mensagem';
        case 'agendamentos':
            return 'Agendamentos';
        case 'whatsapp':
            return 'WhatsApp';
        default:
            return type;
    }
};

const getOptionIcon = (type: string, icon?: LucideIcon) => {
    if (icon) return icon;

    switch (type.toLowerCase()) {
        case 'edit':
        case 'editar':
            return SquarePen;
        case 'delete':
        case 'remover':
            return Trash2;
        case 'history':
        case 'histórico':
        case 'historico':
            return History;
        case 'mensagem':
            return MessageCircleMore;
        case 'agendamentos':
            return CalendarDays;
        case 'whatsapp':
            return MessageCircle;
        default:
            return CircleDot;
    }
};

const OptionIcon = ({ type, icon, className }: { type: string; icon?: LucideIcon; className?: string }) => {
    const Icon = getOptionIcon(type, icon);
    return <Icon className={className} aria-hidden="true" />;
};

const TableActions: React.FC<TableActionsProps> = ({ row, options, children, dropdown = false, disabled = false }) => {
    const { setParams } = useParamsContext();
    const [open, setOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const menuRef = useRef<HTMLUListElement | null>(null);

    const updateMenuPosition = () => {
        if (!buttonRef.current) return;
        const rect = buttonRef.current.getBoundingClientRect();
        const width = MENU_WIDTH;

        let left = rect.right - width;
        left = Math.max(MENU_GUTTER, Math.min(left, window.innerWidth - width - MENU_GUTTER));

        let top = rect.bottom + MENU_OFFSET;
        const menuHeight = menuRef.current?.offsetHeight || 0;
        if (menuHeight && top + menuHeight > window.innerHeight - MENU_GUTTER) {
            top = rect.top - menuHeight - MENU_OFFSET;
        }

        setMenuPosition({ top, left });
    };

    const handleOptionSelect = (option: Option) => {
        if (disabled || option.disabled) {
            return;
        }

        setOpen(false);
        if (typeof option.onClick === 'function') {
            option.onClick(row);
            return;
        }

        setParams({
            action: option.type,
            id: row?._id || row?.id || row?.uid,
        });
    };

    useLayoutEffect(() => {
        if (!open) return;
        updateMenuPosition();
    }, [open, options?.length]);

    useEffect(() => {
        if (!open) return;

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (menuRef.current?.contains(target)) return;
            if (buttonRef.current?.contains(target)) return;
            setOpen(false);
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false);
        };

        const handleResize = () => updateMenuPosition();
        const handleScroll = () => updateMenuPosition();

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleScroll, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [open]);

    return (
        <>
            <ul className="flex items-center gap-1">
                {!!children && children}

                {dropdown ? (
                    <li>
                        <button
                            ref={buttonRef}
                            type="button"
                            aria-label="Abrir acoes"
                            className="sf-icon-button btn btn-circle btn-sm"
                            disabled={disabled}
                            onClick={(event) => {
                                event.stopPropagation();
                                setOpen((prev) => !prev);
                            }}
                        >
                            <EllipsisVertical aria-hidden="true" />
                        </button>
                    </li>
                ) : (
                    options?.map((option, index) => {
                        const optionDisabled = disabled || option.disabled;
                        return (
                            <li key={index}>
                                <button
                                    type="button"
                                    className={`sf-icon-button btn btn-circle btn-xs ${optionDisabled ? 'pointer-events-none opacity-35' : ''}`}
                                    disabled={optionDisabled}
                                    aria-label={getOptionLabel(option.type, option.label)}
                                    title={getOptionLabel(option.type, option.label)}
                                    onClick={() => handleOptionSelect(option)}
                                >
                                    <OptionIcon type={option.type} icon={option.icon} />
                                </button>
                            </li>
                        );
                    })
                )}
            </ul>

            {dropdown &&
                open &&
                createPortal(
                    <ul
                        ref={menuRef}
                        role="menu"
                        style={{
                            position: 'fixed',
                            top: menuPosition.top,
                            left: menuPosition.left,
                            width: `${MENU_WIDTH}px`,
                        }}
                        className="dropdown-content z-[10001] rounded-2xl p-2"
                    >
                        {options?.map((option, index) => {
                            const optionDisabled = disabled || option.disabled;
                            return (
                                <li key={index} className="mb-1 last:mb-0">
                                    <button
                                        type="button"
                                        className={`sf-menu-item flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold ${
                                            optionDisabled ? 'pointer-events-none opacity-35' : ''
                                        }`}
                                        disabled={optionDisabled}
                                        onClick={() => handleOptionSelect(option)}
                                    >
                                        <OptionIcon type={option.type} icon={option.icon} className="w-4 text-sm" />
                                        <span>{getOptionLabel(option.type, option.label)}</span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>,
                    document.body,
                )}
        </>
    );
};

export default TableActions;
